/**
 * PipelineTab.tsx — the ONE pipeline host for BOTH roles (2026-07-21, PR 3/5).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * "That needs to be shown to both admins and to clinicians" — both roles see
 * all 9 columns; the role's own queue column is emphasized (clinician →
 * Callback/Clinician follow-up; admin → Checking eligibility/Admin
 * follow-up). This tab is the SINGLE owner of every manual-move dialog: a
 * board drop, a status-map click and an Overview action all funnel into
 * `requestMove`, which runs the dialog the move's channel demands —
 *   handover  → note (required) + optional scheduled call TIME BLOCK
 *               (→ clinician; UX Wave 9 §5 — the five-block Select, no
 *               datetime picker)
 *   convert   → phone (prefilled from the contact) + preferred time BLOCK
 *   stage-patch closed_lost → ConfirmDialog with a REQUIRED reason
 * — so the server-side side-effects (ticket sync / ticket mint / audit
 * reason) can never be skipped by a bare drag (R41: the server map is the
 * control; stages.ts is affordance).
 *
 * "Create new lead" (CLINICIAN-only, the SQL sales lane; relabelled from
 * "Create new" in UX Wave 4 §4): contact fields → POST /deals → the new
 * deal's drawer opens STRAIGHT ONTO its Questionnaire tab ("create new →
 * contact → deal → the questionnaire", one motion mid-call). A 409 (contact
 * already has an open deal) is surfaced with the message.
 *
 * FILTER TOOLBAR (2026-07-21 UX pass): the old "SQL deals" toggle is gone —
 * clinician-created deals are ALWAYS staff_sql and already carry the entry
 * tag, so it duplicated the Select below.
 *
 * ⚠️ SEARCH IS A SERVER READ (2026-08-29 — it used to be the client-side filter
 * this block described). The board loads ONE page for all eleven lanes, so
 * filtering what was loaded meant a real patient past that page could be typed
 * in by name and produce nothing, while the lane badge above — which counts the
 * whole tenant — claimed they were there. That is how a paid, correctly-staged
 * patient came to be reported as "not there on admin". `?q=` goes to
 * clinic-service, debounced, and searches every deal in the tenant.
 *
 * The entry-path Select stays CLIENT-side: it is a facet of cards already on
 * screen and needs no round trip. "Show test records" is server-side for its
 * own reason (2026-08-26) — marking a deal as test CLOSES it and the default
 * list EXCLUDES it, so a client-side filter would have nothing to show.
 * Visible to all signed-in staff; the server enforces roles (R41).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  BOARD_PER_LANE,
  DEAL_LANE_OFFSET_MAX,
  activityCursorFor,
  convertDealToCallback,
  createDeal,
  dealHandover,
  getDealCounts,
  getDealLaneMoney,
  getDealDetail,
  getDeals,
  patchDealStage,
  searchDeals,
  type DealListPaging,
} from '../api/deals';
import { ApiError } from '../api/client';
import type { DealCard, DealCounts, DealCreated, DealLaneMoneyResponse, DealStage, UserRole } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { TIME_BLOCKS, TIME_BLOCK_LABELS } from '../lib/timeBlocks';
import { SectionTitleInline } from '../staff/ui';
import { Alert, Button, Card, ErrorState, Field, Select, TextInput } from '../ui/kit';
import { BoardSkeleton } from '../ui/skeletons';
import { MarkUnreachableDialog } from './MarkUnreachableDialog';
import { CloseAsLostDialog } from './CloseAsLostDialog';
import { ConfirmDialog, DialogShell } from '../ui/ConfirmDialog';
import { DealDrawer } from './DealDrawer';
import {
  activeFilterCount,
  EMPTY_FILTERS,
  PipelineFilters,
  type PipelineFilterState,
} from './PipelineFilters';
import { dropOffFilterChipLabel } from './dropOffReasonsStaff';
/* The toolbar's furniture is the ESTATE's (2026-09-05) — FilterChip, the
   Filters button + clear-× pair and the shared control height were local to
   this file until the Patients directory needed the same bar. See
   `ui/filters.tsx`; the FACETS stay here, because they are the pipeline's. */
import { CONTROL_ROW_HEIGHT, FilterChip, FilterChipRow, FiltersTrigger } from '../ui/filters';
import { PipelineBoard, dealName, type BoardPaging } from './PipelineBoard';
import { DEAL_STAGES, ENTRY_LABEL, STAGE_LABEL, type ManualTarget } from './stages';

/**
 * ONE LANE'S EXTRA PAGES (2026-09-04).
 *
 * The base read is page ONE for all eleven lanes; this holds what the lane's
 * own "View more" presses have added on top of it. Kept SEPARATE from `items`
 * rather than appended into it, for two reasons that both bit:
 *
 *  - `items` is replaced wholesale by every refresh (a move, a drawer change,
 *    a filter). Appending into it would mean each refresh either wiped the
 *    pages staff had paged in, or the refresh had to know how to re-fetch
 *    them. Held apart, a refresh replaces page one and the extra pages survive.
 *  - the "has this lane got more?" question is answered by the LAST PAGE
 *    FETCHED for that lane, not by the merged total. Merging first would throw
 *    away the fact the answer depends on.
 */
export interface LanePages {
  /** Cards from View-more presses, in wire order (createdAt desc, id desc). */
  cards: DealCard[];
  /** How many EXTRA pages have landed — the next press asks for page n+2. */
  pages: number;
  /**
   * Did the last page for this lane come back FULL? That is the only "there is
   * more" signal on this wire — the response is a flat `{items}` with no total
   * and no `hasMore` flag, so a lane has another page exactly when the page
   * just fetched for it held `perLane` cards. A lane whose size is an exact
   * multiple of `perLane` therefore costs ONE press that returns nothing and
   * then retires the control; that is the contract's own cost, and it is
   * cheaper than inventing a total the server does not send.
   */
  lastFull: boolean;
  /**
   * THE KEYSET CURSOR (2026-09-26, S4.5) — the last card of the last page that
   * ARRIVED for this lane, as the wire sent it, BEFORE the board's dedupe.
   *
   * ⚠️ NOT `cards.at(-1)`. `cards` is deduped against what the board already
   * holds, so a page whose cards were ALL bumped onto page one (their
   * `lastActivityAt` moved) adds nothing to `cards` — and a cursor read off
   * `cards` would then ask for the same page again, forever. The cursor is the
   * POSITION this lane has been read up to, which is the wire's last card
   * whether or not the board kept it. It is recorded on the offset
   * (old-server) lane too, where nothing reads it: that lane pages by `pages`.
   *
   * `createdAt` rides along (2026-09-26, S4.5 review) so the board can tell
   * whether a card sits BEFORE the cursor in the activity order — the middle
   * key of the order, and the one `healDisplaced` needs to stop at the cursor.
   */
  cursor?: Pick<DealCard, 'dealId' | 'lastActivityAt' | 'createdAt'>;
}

/**
 * IS THE OFFSET LANE AT ITS CEILING? (2026-09-26, S4.5 review — one rule for
 * the two places that ask it.)
 *
 * `pagesHeld` extra pages are on the board, so the next press asks for offset
 * `(pagesHeld + 1) * perLane`, and past clinic-service's `DEAL_LANE_OFFSET_MAX`
 * that read is a 400. The KEYSET has no ceiling — it asks for "after this
 * card", not "skip N" — so on the activity order the answer is always no. The
 * control (`lanePageMore`) and the press (`loadMore`) both read THIS, so the
 * spec that pins it pins both (it used to be two copies of one expression, and
 * dropping `!keyset` from either turned nothing red).
 */
export function pastOffsetCeiling(keyset: boolean, pagesHeld: number): boolean {
  return !keyset && (pagesHeld + 1) * BOARD_PER_LANE > DEAL_LANE_OFFSET_MAX;
}

/**
 * THE ACTIVITY ORDER, CLIENT-SIDE: does `card` sort strictly BEFORE `cursor`?
 *
 * clinic's order is `lastActivityAt desc NULLS LAST, createdAt desc, id desc`
 * (S4.1 `DEAL_LIST_ORDER_BY_ACTIVITY`), and its keyset reads "strictly after
 * the cursor" in that order. This is the same order, asked the other way
 * round, for the one job the board has for it: `healDisplaced` stops at the
 * cursor, because everything from there on is what "View more" reads next.
 * Ids compare as plain strings, as Postgres compares lowercase uuids.
 *
 * ⚠️ NOT SHARED WITH THE STUB. `testing/fixtures.ts` has its own copy of the
 * server's order (`compareByActivity`); a stub that imported this one would
 * move with any mutation of it, and a spec could then never catch that
 * mutation.
 */
export function sortsBeforeCursor(
  card: Pick<DealCard, 'dealId' | 'lastActivityAt' | 'createdAt'>,
  cursor: Pick<DealCard, 'dealId' | 'lastActivityAt' | 'createdAt'>,
): boolean {
  const at = (c: Pick<DealCard, 'lastActivityAt'>) => (c.lastActivityAt ? Date.parse(c.lastActivityAt) : null);
  const mine = at(card);
  const theirs = at(cursor);
  if (mine !== theirs) {
    if (mine === null) return false; // nulls sort last
    if (theirs === null) return true;
    return mine > theirs;
  }
  if (card.createdAt !== cursor.createdAt) return card.createdAt > cursor.createdAt;
  return card.dealId > cursor.dealId;
}

/**
 * THE CARDS A REFRESH PUSHED OFF PAGE ONE, AND THE READ THAT BRINGS THEM BACK
 * (2026-09-26, S4.5 review — the major finding).
 *
 * `load()` runs after every move, drawer change and create. It REPLACES page
 * one and deliberately KEEPS the extra pages and their cursor (staff page a
 * lane in to work through it). On the activity order that leaves a hole: a
 * lane paged to 50 holds page one = 1–25 and a kept page = 26–50, cursor at
 * 50. Card 55 gets activity (an order-sync, a new questionnaire, a callback)
 * and rises to the top, so the next page one is 55 + 1–24. Card 25 is then in
 * NEITHER list — not drawn — and it sits BEFORE the cursor, so no later "View
 * more" returns it. It stayed invisible until the filters changed, while the
 * lane badge still counted it: the C-15 skip ("SKIPPED a patient nobody would
 * then see"), on a card that did not move.
 *
 * So after a refresh, a lane with kept pages whose OLD page one has a card the
 * new page one and the kept pages both lack is re-read from the new page
 * one's last card, forward, up to the first card the board already holds (or
 * the cursor): exactly the gap. Server truth, never a guess from the old
 * read — a card that LEFT the lane (moved, marked test, merged) is simply not
 * returned, where carrying the old copy over would draw it in a lane it has
 * left.
 *
 * `lanesToHeal` is the trigger (pure, so a spec can pin it without a fixture
 * of fifty cards); `gapBeforeHeld` is the stop rule for one arriving page.
 * The created-order (old-server) lane has the same hole when a NEW card enters
 * page one, and is not healed: it exists only while clinic is older than S4.1.
 */
export function lanesToHeal(
  previous: readonly DealCard[],
  next: readonly DealCard[],
  lanePages: Partial<Record<DealStage, LanePages>>,
): DealStage[] {
  const onNextPageOne = new Set(next.map((c) => c.dealId));
  const lanes: DealStage[] = [];
  for (const [lane, held] of Object.entries(lanePages) as [DealStage, LanePages | undefined][]) {
    if (!held || held.pages < 1 || !held.cursor) continue;
    // A page one that is not full holds the whole lane: there is no page two
    // for a card to have fallen into.
    if (next.filter((c) => c.stage === lane).length < BOARD_PER_LANE) continue;
    const kept = new Set(held.cards.map((c) => c.dealId));
    if (previous.some((c) => c.stage === lane && !onNextPageOne.has(c.dealId) && !kept.has(c.dealId))) lanes.push(lane);
  }
  return lanes;
}

/**
 * One arriving gap page, cut at the first card that ends the gap: one the
 * board already holds on a kept page (from there on the kept pages are the
 * lane, contiguous), or one that does not sort before the cursor (from there
 * on is what "View more" reads next). `done` says the cut was found.
 */
export function gapBeforeHeld(
  arriving: readonly DealCard[],
  kept: ReadonlySet<string>,
  cursor: Pick<DealCard, 'dealId' | 'lastActivityAt' | 'createdAt'>,
): { cards: DealCard[]; done: boolean } {
  const cards: DealCard[] = [];
  for (const card of arriving) {
    if (kept.has(card.dealId) || !sortsBeforeCursor(card, cursor)) return { cards, done: true };
    cards.push(card);
  }
  return { cards, done: false };
}

/** How many gap reads one refresh may spend on one lane (25 cards each). */
const GAP_READS_MAX = 3;

/**
 * HAS THIS LANE GOT ANOTHER PAGE? (2026-09-04)
 *
 * `held` is the lane's extra pages, `basePageCount` its slice of PAGE ONE — the
 * base read, never the merged board, because a full page one is the thing that
 * says "there may be a page two".
 *
 * ⚠️ THE CEILING IS CHECKED FIRST. Past clinic-service's `DEAL_LANE_OFFSET_MAX`
 * the read is a 400, and a control that 400s on press is worse than a control
 * that is not there — the board would report "that page did not load" when the
 * truth is that there is nowhere further to go. Forty pages down one lane is
 * not a board anyone works, which is exactly why the guard needs a test rather
 * than a reader's confidence.
 *
 * Its own function so the ceiling and the full-page rule are provable without
 * a fixture holding a thousand deals.
 */
export function lanePageMore(held: LanePages | undefined, basePageCount: number, keyset = false): boolean {
  // The offset ceiling is the OFFSET lane's (a server 400 past it). The keyset
  // has no such bound — it asks for "after this card", not "skip N" — so on the
  // activity order a deep lane stays pageable (2026-09-26, S4.5).
  if (pastOffsetCeiling(keyset, held?.pages ?? 0)) return false;
  return held ? held.lastFull : basePageCount === BOARD_PER_LANE;
}

/**
 * Fold an arriving page into a lane's held pages. Deduped by `dealId`.
 *
 * On the OFFSET lane (the created order) this is belt-and-braces: that order is
 * `createdAt desc, id desc` and stable across pages, so page N+1 never repeats
 * page N. On the ACTIVITY lane (S4.5) it is load-bearing: a card bumped between
 * pages moves AHEAD of the cursor, and the next page-one refresh brings it back
 * at the top of its lane — so `seen` also holds the board's own page one
 * (`alsoOnBoard`), and a card is drawn once, where the fresher read put it.
 *
 * `lastFull` is judged on the RAW page (what the server sent for this lane),
 * never on what survived the dedupe: a full page with one bumped card is still
 * a full page, and judging it short would retire "View more" one page early.
 */
function mergeLanePage(
  held: LanePages | undefined,
  arriving: DealCard[],
  page: number,
  alsoOnBoard: ReadonlySet<string> = new Set(),
): LanePages {
  const existing = held?.cards ?? [];
  const seen = new Set([...existing.map((c) => c.dealId), ...alsoOnBoard]);
  const last = arriving.at(-1);
  return {
    cards: [...existing, ...arriving.filter((c) => !seen.has(c.dealId))],
    pages: page,
    lastFull: arriving.length === BOARD_PER_LANE,
    // An empty page moves no cursor — there is nothing further to have read.
    cursor: last ? { dealId: last.dealId, lastActivityAt: last.lastActivityAt, createdAt: last.createdAt } : held?.cursor,
  };
}

/**
 * WHICH ORDER THE BOARD IS READING IN (2026-09-26, S4.5).
 *
 * `null` until the first board read answers; then `true` (clinic-service took
 * `order=activity`) or `false` (it answered that read with a 400 — a
 * clinic-service older than S4.1, whose `.strict()` query schema has never
 * heard of `order`). Decided by the SERVER'S ANSWER, never guessed, because the
 * two paging modes cannot be mixed: the keyset only means anything in the
 * activity order, and `offset` over the activity order skips cards (C-15).
 *
 * It can go `true` → `false` mid-session (a clinic rollback below #224 under
 * an open board — see `shouldFallBackToCreatedOrder`), and then the kept
 * pages go with it: they were read in the other order. It never goes back to
 * `true` without a reload; a board that fell back reads exactly as it did
 * before S4.5.
 */
export type ActivityOrderSupport = boolean | null;

/**
 * Should a failed page-one read be retried in the created order? When the
 * activity order was ASKED for and the answer was a 400. A 5xx, a 403 or a
 * network fault is a real failure and keeps today's error handling.
 *
 * ⚠️ WHETHER OR NOT THE SERVER HAS SERVED THE ORDER BEFORE (2026-09-26, S4.5
 * review). The first cut asked only while support was still unknown, so a
 * clinic rolled back below #224 under an open board — `activityOrder` latched
 * `true` — turned the next refresh (after ANY move) into the eleven-lane
 * error state, and "Try again" re-sent the same refused read until a reload.
 * That is the outcome the fallback exists to prevent. What stops a 400 about
 * something else from flipping the order is `load()`'s rule, not this one:
 * the order is switched only when the created-order re-read SUCCEEDS, and
 * that read differs from the refused one by `order` alone — so a 400 the
 * created order also refuses was never about the order, and changes nothing.
 */
export function shouldFallBackToCreatedOrder(error: unknown): boolean {
  return error instanceof ApiError && error.status === 400;
}

/** A move awaiting its dialog (the owning channel decides which one shows). */
interface PendingMove {
  card: DealCard;
  target: ManualTarget;
}

const compactButton = { width: 'auto', minHeight: '44px', padding: '10px 18px' } as const;

/**
 * HOW MUCH WIDTH THE TOOLBAR CLAIMS ON THE TITLE LINE — and therefore WHERE IT
 * WRAPS BACK OFF IT (2026-09-06).
 *
 * A flex-basis rather than a media query, because the thing that decides
 * whether these two fit on one line is the CARD's width, not the window's. The
 * board already lives inside PageFrame's 1100px `<main>`, and a media query on
 * the viewport would be measuring the wrong box the first time this Card is
 * used anywhere narrower.
 *
 * The number is measured, not taste. "Pipeline" renders 71px wide and the row's
 * column gap is 16px, so a 640px basis makes the pair 727px — and the flex line
 * wraps as soon as the Card's content box is narrower than that. Re-measured in
 * a browser on 2026-09-07, after the label moved INTO the toolbar row and the
 * row became one line for the first time: content 729px at a 1071px viewport
 * renders ONE row, content 726px at 1068px renders TWO. The threshold is
 * therefore 727px of content to the pixel, which is the constant plus the
 * heading plus the gap, and it lands at about a 1070px viewport — the "below
 * roughly 1100px it goes back to two rows" the design asks for. At the narrowest
 * width the staff console will render at all (768px, `auth/DesktopOnly`) the row
 * is wrapped and the page still does not scroll sideways.
 *
 * ⚠️ IT IS THE CARD'S WIDTH THAT DECIDES, NOT THE WINDOW'S, so the viewport
 * figures above are true of the ADMIN console and nothing else — that rail is
 * ~300px wide expanded and ~44px collapsed, and collapsing it keeps the row
 * unwrapped down to about a 775px viewport. A media query would have hard-coded
 * one of those two answers and been wrong about the other. The CLINICIAN console
 * is the other proof of the same point: "Create new lead" sits beside the
 * heading, so its left block is 226px rather than 71px and the row wraps at
 * 882px of content (measured: 888px one row, 874px two) — a ~1230px viewport.
 * Same constant, same rule, two different windows.
 *
 * ⚠️ WRAPPING IS A SUPPORTED STATE, NOT A DEGRADED ONE. Below the threshold the
 * toolbar drops to its own full-width line and the screen is what it was before
 * this change. Nothing may be built on the height this row gives back — the
 * lane cap is a share of the viewport and the money total is pinned inside its
 * own lane, so both behave identically wrapped or not.
 */
/**
 * How much of the title line the toolbar asks for before it wraps.
 *
 * ⚠️ RAISED 640 → 780 ON 2026-09-13, when "Create new lead" joined the row. The
 * toolbar now carries four items rather than three, and at the old basis the
 * button was the one that wrapped — a primary action dropping to its own line
 * under the search box, which is the layout the CTO had just asked to leave.
 */
const TOOLBAR_INLINE_BASIS = '780px';

export function PipelineTab() {
  const { me } = useAuth();
  const role: UserRole = me?.role ?? 'CLINICIAN';
  const [items, setItems] = useState<DealCard[] | null>(null);
  const [counts, setCounts] = useState<DealCounts | null>(null);
  /**
   * The lane money panel's data. Null means "no figures" for one of THREE
   * different reasons, and the footer has to distinguish them:
   * a CLINICIAN (never fetched — money is ADMIN-only), a read that failed, or
   * a read that has not landed yet. The footer renders nothing at all for the
   * first and an admission for the second, which is why this is not folded
   * into `counts` — a shared null would collapse "not for you" into "broken".
   */
  const [laneMoney, setLaneMoney] = useState<DealLaneMoneyResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  /**
   * A SEARCH that failed, as distinct from a BOARD that failed.
   *
   * ⚠️ WHY THESE ARE TWO DIFFERENT THINGS (2026-08-29, caught in stage
   * pre-flight). The deals read is shared: the board load and the search are
   * the same request with a different query. Before this, ANY failure of it set
   * `status: 'error'`, which swaps the entire eleven-lane board for "Sorry — we
   * could not load the pipeline." So a single bad search — a 400 from a term
   * the server rejects, a transient 500, or a clinic-service that has not yet
   * been promoted — took the whole working surface away from every clinician
   * and admin on the tenant, and only clearing the box brought it back.
   *
   * A search that cannot run is a small, local problem. It must read as one.
   */
  const [searchError, setSearchError] = useState(false);
  const [search, setSearch] = useState('');
  /**
   * The search term the BOARD IS LOADED FOR, as opposed to the one being typed.
   *
   * Two pieces of state, not one, and the separation is what makes the box
   * usable: `search` updates on every keystroke so the input stays responsive,
   * while `debouncedSearch` is what the fetch keys on. One shared value would
   * either fire a request per character or lag the caret behind the typist.
   */
  const [debouncedSearch, setDebouncedSearch] = useState('');
  /**
   * ONE APPLIED STATE, not three separate ones (2026-08-30). The panel edits a
   * draft and commits it in a single press, so the board re-reads once per
   * Apply rather than once per checkbox — four ticks used to mean four server
   * reads, three of them describing a filter nobody asked for.
   */
  const [filters, setFilters] = useState<PipelineFilterState>(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<DealCard | null>(null);
  const [pending, setPending] = useState<PendingMove | null>(null);
  /**
   * The close-as-lost dialog's OWN failure and busy state.
   *
   * ⚠️ NOT `notice`. A refusal reported through `setNotice` renders an Alert on
   * the BOARD — behind the drawer's fixed scrim and outside its `aria-modal`
   * subtree. That is how "Close as lost" came to look like it did nothing: the
   * server's sentence was correct and invisible. The dialog stays open and owns
   * its own error; see `CloseAsLostDialog`'s header.
   */
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // A just-created deal to auto-open on its Questionnaire tab once it lands.
  const [openOnCreate, setOpenOnCreate] = useState<string | null>(null);
  /** Extra per-lane pages, keyed by stage — see LanePages. */
  const [lanePages, setLanePages] = useState<Partial<Record<DealStage, LanePages>>>({});
  /** A lane's press in flight, or one that failed and is still retryable. */
  const [laneStatus, setLaneStatus] = useState<Partial<Record<DealStage, 'loading' | 'error'>>>({});
  /** The last arrival in words, for the board's one polite live region. */
  const [laneAnnouncement, setLaneAnnouncement] = useState<string | null>(null);
  /**
   * WHICH QUERY THE PAGED READS BELONG TO.
   *
   * ⚠️ A PAGE CAN LAND AFTER THE BOARD HAS MOVED ON. Press "View more", then
   * apply a filter before the response arrives: the reset below has already
   * dropped the held pages, and the in-flight `.then` would insert twenty-five
   * cards from the UNFILTERED query into the filtered board — deals that do not
   * match the filter, sitting in a lane under deals that do. On a board where
   * which column a card is in decides who gets seen, that is not a cosmetic
   * race.
   *
   * A counter, not an AbortController: the read is shared with the board load
   * through `getDeals`, and aborting is a change to that seam for every caller.
   * Stamping the response and dropping a stale one is local to this component
   * and cannot affect anything else that reads deals.
   */
  const queryGeneration = useRef(0);
  /**
   * Did clinic-service accept the ACTIVITY order (2026-09-26, S4.5)? A ref, not
   * state: it is decided by a read's answer and read by the NEXT press, and
   * re-rendering on it would re-fire the board load for nothing. Set in the
   * same `.then` that hands the page to `setItems`, so the render that draws
   * the lanes already knows which paging mode their "View more" is in.
   */
  const activityOrder = useRef<ActivityOrderSupport>(null);
  /**
   * The page one and the kept pages AS LAST DRAWN, for `healDisplaced`
   * (2026-09-26, S4.5 review). `load` is keyed on the query and deliberately
   * not on the data (see the note at the end of it), so it reads them here
   * rather than through its closure. Synced in effects, never during render.
   */
  const drawnPageOne = useRef<DealCard[] | null>(null);
  const drawnLanePages = useRef<Partial<Record<DealStage, LanePages>>>({});
  useEffect(() => {
    drawnPageOne.current = items;
  }, [items]);
  useEffect(() => {
    drawnLanePages.current = lanePages;
  }, [lanePages]);

  /**
   * OPEN ANOTHER DEAL BY ID — how the duplicate pair is reachable from either
   * end (2026-09-06, CTO: "from either card you can reach the other").
   *
   * ⚠️ THE LOADED PAGE IS NOT ENOUGH, AND THAT IS THE WHOLE REASON THIS EXISTS.
   * `setSelected` needs a `DealCard` and every other route into the drawer has
   * one to hand, because the user clicked it. A merged duplicate is precisely
   * the card the board does NOT hold: clinic-service hides duplicates from the
   * working lanes by default, so following a `duplicateOfDealId` — or any of
   * the canonical's `mergedDuplicates` — usually means fetching it.
   *
   * The page is still checked FIRST: when the counterpart happens to be on
   * screen (the `Merged duplicate` lane is open, or the pair are siblings in a
   * search result) the swap is instant and costs no request.
   *
   * ONE READ, AND IT IS THE ONE THE DRAWER WOULD MAKE ANYWAY. `getDealDetail`
   * returns the full aggregate whose `deal` IS the card, so the fetch is not
   * overhead — the drawer re-issues it on mount and this simply pays for it a
   * moment early rather than inventing a second lookup endpoint.
   *
   * ⚠️ A FAILURE IS THE CALLER'S TO REPORT, AND IT USED TO BE REPORTED WHERE
   * NOBODY COULD SEE IT.
   *
   * This used to `setNotice(...)` on a rejection, which renders an Alert ON THE
   * BOARD. But the only thing that calls this is a link INSIDE `DealDrawer` —
   * a `position: fixed` scrim wrapping a `role="dialog" aria-modal="true"`. So
   * the notice landed in a region the modal covers and `aria-modal` removes
   * from the accessibility tree entirely: a screen-reader user got nothing, a
   * narrow viewport got nothing, and a wide one got a dimmed strip behind a 45%
   * scrim while the drawer sat unchanged. The measured result was NOTICE=true,
   * DIALOG STILL OPEN=true, NOTICE INSIDE DIALOG=false — which is
   * indistinguishable, to the person clicking, from the silent no-op the
   * comment below says is the one unacceptable outcome.
   *
   * So the promise is RETURNED and left to reject. The drawer owns the failure
   * because the drawer owns the surface the user is looking at; see
   * `openLinkedDeal` in DealDrawer.tsx. Nothing here catches it — a swallowed
   * rejection is exactly the bug being fixed.
   *
   * Silently doing nothing on a click is the one outcome that reads as a broken
   * link, and rendering the apology underneath an opaque modal IS doing nothing.
   */
  const openDealById = useCallback(
    async (dealId: string) => {
      const onScreen = items?.find((card) => card.dealId === dealId);
      if (onScreen) {
        setSelected(onScreen);
        return;
      }
      const detail = await getDealDetail(dealId);
      setSelected(detail.deal);
    },
    [items],
  );

  /**
   * SEARCH IS A SERVER READ NOW (2026-08-29).
   *
   * It used to filter the loaded page in the browser, which is why a real
   * patient past the first page could be typed in by name and produce nothing
   * while the lane badge above claimed they were there. clinic-service takes
   * `?q=` and searches the whole tenant, so the box finally means what it looks
   * like it means.
   *
   * 300ms, chosen against the two failure modes rather than by feel: shorter
   * and an average typist fires a request per character; longer and the board
   * visibly lags behind a finished word. The timer is cleared on every change,
   * so only the pause at the end of typing survives to become a fetch.
   */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  /**
   * RE-READ WHAT A REFRESH PUSHED OFF PAGE ONE (2026-09-26, S4.5 review) —
   * see `lanesToHeal` for the hole this closes.
   *
   * For each lane that needs it: read from the NEW page one's last card,
   * forward, and keep the cards before the first one the board already holds
   * on a kept page (or the cursor), at most `GAP_READS_MAX` reads. The gap
   * goes in FRONT of the kept cards, because it sits between page one and
   * them. The cursor, `pages` and `lastFull` are untouched: they describe how
   * far "View more" has read, and this read is behind that point.
   *
   * Silent by design. It is a repair, not a press: no busy state, no
   * announcement, and a failure leaves the board as it was (the next refresh
   * tries again). A query change mid-read drops the answer (the generation).
   */
  const healDisplaced = useCallback(
    (previous: DealCard[], next: DealCard[]) => {
      const kept = drawnLanePages.current;
      const lanes = lanesToHeal(previous, next, kept);
      if (lanes.length === 0) return;
      const generation = queryGeneration.current;
      const onPageOne = new Set(next.map((c) => c.dealId));
      for (const lane of lanes) {
        const held = kept[lane];
        const from = next.filter((c) => c.stage === lane).at(-1);
        if (!held?.cursor || !from) continue;
        const cursor = held.cursor;
        const heldIds = new Set(held.cards.map((c) => c.dealId));
        void (async () => {
          const gap: DealCard[] = [];
          let after: Pick<DealCard, 'dealId' | 'lastActivityAt'> = from;
          for (let read = 0; read < GAP_READS_MAX; read += 1) {
            const page = await getDeals([lane], filters.showTest, 0, filters.showDuplicates, filters.dropOffReasons, {
              cursor: activityCursorFor(after),
            });
            if (queryGeneration.current !== generation) return;
            const arriving = page.items.filter((c) => c.stage === lane);
            const cut = gapBeforeHeld(arriving, heldIds, cursor);
            gap.push(...cut.cards.filter((c) => !onPageOne.has(c.dealId)));
            const last = arriving.at(-1);
            if (cut.done || arriving.length < BOARD_PER_LANE || !last) break;
            after = last;
          }
          if (gap.length === 0) return;
          setLanePages((prev) => {
            const current = prev[lane];
            if (!current) return prev;
            const have = new Set(current.cards.map((c) => c.dealId));
            const add = gap.filter((c) => !have.has(c.dealId));
            return add.length === 0 ? prev : { ...prev, [lane]: { ...current, cards: [...add, ...current.cards] } };
          });
        })().catch(() => undefined);
      }
    },
    [filters],
  );

  const load = useCallback(() => {
    setStatus((prev) => (prev === 'ready' ? prev : 'loading'));
    // filters.showTest → includeTest=true on the wire; the default read never carries
    // the param, so the working board can never quietly include test records.
    //
    // ⚠️ THE COUNTS READ IS DELIBERATELY NOT GIVEN THE SEARCH TERM. The badges
    // are the pipeline's real per-stage totals and staff read them as such;
    // re-counting them against a filter would silently turn the board's one
    // fixed reference point into a second, differently-scoped search result.
    // PipelineBoard suppresses its "N of M" cue while filtering for exactly
    // this reason.
    setSearchError(false);
    // ⚠️ TWO CALLS, ONE SHAPE. A search goes to POST …/deals/search with the
    // term in the BODY — a patient's name must never reach a URL, because GCLB
    // logs query strings for 400 days (see `searchDeals`). The unsearched board
    // stays a plain GET. Both return `dealList`, so nothing downstream cares
    // which one answered.
    // `filters.showDuplicates` → `includeDuplicates=true` (2026-09-06). Without
    // it the server refuses to serve a merged card at all, which is why the
    // `closed_duplicate` lane rendered as an empty column even with cards in
    // it. Separate from `showTest` on both reads — see PipelineFilterState.
    //
    // ⚠️ THE ACTIVITY ORDER, WITH AN OLD-SERVER FALLBACK (2026-09-26, S4.5 —
    // plan Addendum 3). Page one asks for `order=activity` (newest ACTIVITY
    // first, so a returning patient's new questionnaire lifts their card), and
    // "View more" then pages by keyset. A clinic-service older than S4.1 400s
    // the unknown param on its `.strict()` schema — and a 400 on this read
    // would swap the whole eleven-lane board for the error state for every
    // clinician and admin on the tenant. So a 400 on an activity read re-asks
    // once in the created order and, when THAT answers, remembers it: the board
    // then reads and pages exactly as it did before S4.5 (`offset`), which is
    // what that server understands. See `shouldFallBackToCreatedOrder` — and
    // it holds mid-session too, for a clinic rolled back under an open board.
    const readInOrder = (activity: boolean) =>
      debouncedSearch.length >= 2
        ? searchDeals(debouncedSearch, filters.showTest, filters.stages, filters.showDuplicates, filters.dropOffReasons, activity)
        : getDeals(
            filters.stages,
            filters.showTest,
            0,
            filters.showDuplicates,
            filters.dropOffReasons,
            activity ? { order: 'activity' } : undefined,
          );
    const askActivity = activityOrder.current !== false;
    /** Did THIS read switch a board that was paging by keyset to the created order? */
    let leftActivityOrder = false;
    const read = readInOrder(askActivity).then(
      (deals) => {
        if (askActivity) activityOrder.current = true;
        return deals;
      },
      (error: unknown) => {
        if (!askActivity || !shouldFallBackToCreatedOrder(error)) throw error;
        // ⚠️ SWITCH ONLY ON THE RE-READ'S SUCCESS. The two reads differ by
        // `order` alone, so a 400 the created order ALSO refuses was about
        // something else, and must leave the order as it was.
        return readInOrder(false).then((deals) => {
          leftActivityOrder = activityOrder.current === true;
          activityOrder.current = false;
          return deals;
        });
      },
    );
    // ⚠️ THE MONEY READ IS FIRED ONLY FOR AN ADMIN. clinic-service refuses a
    // CLINICIAN with a 403 (the route is `@Roles('ADMIN')`), so calling it as
    // one would be a guaranteed failed request on every board load — and the
    // resulting `null` is indistinguishable from a real outage. Gating the
    // CALL keeps the footer's three states honest; the server's guard is what
    // actually enforces "clinicians never see money".
    const money = role === 'ADMIN' ? getDealLaneMoney().catch(() => null) : Promise.resolve(null);
    Promise.all([read, getDealCounts().catch(() => null), money])
      .then(([deals, liveCounts, liveMoney]) => {
        const previous = drawnPageOne.current;
        if (leftActivityOrder) {
          // The kept pages and their cursors were read in the ACTIVITY order,
          // and the board now pages by `offset` in the created one — the two
          // modes are never mixed. Dropped exactly as a query change drops
          // them, in-flight pages included (the generation moves on).
          queryGeneration.current += 1;
          setLanePages({});
          setLaneStatus({});
          setLaneAnnouncement(null);
        }
        setItems(deals.items);
        setCounts(liveCounts);
        setLaneMoney(liveMoney);
        setStatus('ready');
        if (activityOrder.current === true && previous) healDisplaced(previous, deals.items);
      })
      .catch(() => {
        // A FAILED SEARCH LEAVES THE BOARD STANDING. `hadBoard` is read through
        // the setter so this closure never depends on a stale `items` — the
        // callback identity is keyed on the fetch inputs, not on the data.
        setItems((current) => {
          const hadBoard = current !== null;
          if (hadBoard && debouncedSearch !== '') setSearchError(true);
          else setStatus('error');
          return current;
        });
      });
    // WHY filters.showTest and debouncedSearch are deps: both change what the SERVER
    // returns, so both must re-fetch; the load effect below re-runs off this
    // identity. `role` joins them (2026-09-06) because it decides whether the
    // money read fires at all — without it, a session that resolves its
    // principal after the first load would leave an admin looking at a board
    // with no money on it until they touched a filter. `healDisplaced` is keyed
    // on `filters` alone, so it adds no new reason to re-fetch.
  }, [filters, debouncedSearch, role, healDisplaced]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * A NEW QUERY DROPS THE PAGED-IN CARDS; A REFRESH OF THE SAME QUERY KEEPS
   * THEM (2026-09-04).
   *
   * ⚠️ THIS IS DELIBERATELY NOT INSIDE `load`. `load()` is called for two
   * different reasons and they need opposite answers. Changing the filters or
   * the search term makes the extra pages describe a query nobody is looking at
   * any more — page two of "Plan sent, unfiltered" is not page two of "Plan
   * sent, Widget form" — so they must go. But `load()` is ALSO called after
   * every move, every drawer change and every create, and dropping the pages
   * there would yank twenty-five cards out from under someone who had just
   * paged them in to work through them.
   *
   * Keying this effect on the same inputs `load` is keyed on separates the two:
   * this fires only when the QUERY changed, and a bare `load()` leaves it alone.
   */
  useEffect(() => {
    queryGeneration.current += 1;
    setLanePages({});
    setLaneStatus({});
    setLaneAnnouncement(null);
  }, [filters, debouncedSearch]);

  /**
   * "VIEW MORE" ON ONE LANE (2026-09-04, clinic-service `offset`).
   *
   * Scoped to the pressed lane and nothing else: `stages=[lane]` + `perLane` +
   * `offset`, so the read costs one lane's page rather than re-reading all
   * eleven to grow one of them.
   *
   * ⚠️ THERE IS NO SEARCHED BRANCH HERE, AND THAT IS NOT AN OMISSION. The board
   * withholds the paging control entirely while a search or filter is running
   * (`laneViewMore` — `items` is then the post-filter list, so "rendered" means
   * matched rather than loaded), so a "page a searched lane" press is not a
   * state that exists. A `searchDeals(…, offset)` call here would be a path
   * nothing can reach and therefore nothing can test — see `searchDeals` in
   * api/deals.ts, which records the POST body's own offset contract for the day
   * that changes.
   *
   * ⚠️ THE OFFSET IS COUNTED IN PAGES ASKED FOR, NOT CARDS HELD. `pages` is how
   * many extra pages this lane has fetched, so the next offset is
   * `(pages + 1) * perLane` — the row the server should skip to. Deriving it
   * from `cards.length` instead would go wrong the moment a page came back
   * short or a card was deduped, and would then re-request rows already on
   * screen.
   *
   * A press for a lane already in flight is IGNORED rather than queued: a
   * double-press must not fetch the same page twice, and the control stays
   * focusable (it is `aria-busy`, not `disabled`) precisely so the second press
   * is possible and therefore has to be handled here.
   */
  const loadMore = useCallback(
    (lane: DealStage) => {
      if (laneStatus[lane] === 'loading') return;
      const held = lanePages[lane];
      const nextPage = (held?.pages ?? 0) + 1;
      const offset = nextPage * BOARD_PER_LANE;
      /**
       * ⚠️ THE KEYSET, NEVER `offset`, ON THE ACTIVITY ORDER (2026-09-26, S4.5 —
       * plan Addendum 3). The cursor is the last card this lane has been READ up
       * to: the last card of the last page that arrived, or — before any press —
       * the last card of this lane's slice of page one, in wire order. The
       * server refuses `offset` beside the activity order, and would be right
       * to: the order moves, so "skip 25" skips whoever moved.
       */
      const keyset = activityOrder.current === true;
      let cursorPaging: DealListPaging | undefined;
      if (keyset) {
        const cursorCard = held?.cursor ?? (items ?? []).filter((c) => c.stage === lane).at(-1);
        // A TYPE GUARD, NOT A BEHAVIOUR: no spec reaches it, because no human
        // can. The control is drawn only for a FULL page one or a held page
        // (`lanePageMore`), and either has a card to read after.
        if (!cursorCard) return;
        cursorPaging = { cursor: activityCursorFor(cursorCard) };
      }
      // The board should already have withdrawn the control at the ceiling
      // (see `lanePaging`); this is the belt to that braces — an offset over
      // the cap is a 400, and a 400 here would read as "the load failed" when
      // the truth is "there is nowhere further to go". The keyset has no cap:
      // the SAME rule the control reads (`pastOffsetCeiling`).
      if (pastOffsetCeiling(keyset, held?.pages ?? 0)) return;
      // Stamped at PRESS time; both handlers below drop a response whose query
      // is no longer the one on screen — see `queryGeneration`.
      const generation = queryGeneration.current;
      setLaneStatus((prev) => ({ ...prev, [lane]: 'loading' }));
      // The lane page must carry the SAME duplicate scope as the board read
      // that produced page one, or "View more" on `closed_duplicate` would
      // fetch a page the server refuses to fill and the lane would look like
      // it had run out one page in. The same goes for the reason filter
      // (2026-09-11): without it, page two of a filtered lane would be page two
      // of the UNFILTERED lane, appended under chips that say otherwise.
      const pageRead = cursorPaging
        ? getDeals([lane], filters.showTest, 0, filters.showDuplicates, filters.dropOffReasons, cursorPaging)
        : getDeals([lane], filters.showTest, offset, filters.showDuplicates, filters.dropOffReasons);
      pageRead
        .then((page) => {
          if (queryGeneration.current !== generation) return;
          // The read is stage-scoped, so every row SHOULD be this lane's. The
          // filter costs nothing and means a card can never be appended into a
          // lane it does not belong to.
          const arriving = page.items.filter((c) => c.stage === lane);
          // Page one's own ids join the dedupe: on the activity order a card
          // bumped between pages is already back on page one (S4.5).
          const onBoard = new Set((items ?? []).map((c) => c.dealId));
          const merged = mergeLanePage(held, arriving, nextPage, onBoard);
          setLanePages((prev) => ({ ...prev, [lane]: mergeLanePage(prev[lane], arriving, nextPage, onBoard) }));
          setLaneStatus((prev) => {
            const { [lane]: _done, ...rest } = prev;
            return rest;
          });
          // R19: ids, enums and integers — a lane label and two counts. No
          // name, no email, nothing about a patient.
          const added = merged.cards.length - (held?.cards.length ?? 0);
          const onScreen = (items ?? []).filter((i) => i.stage === lane).length + merged.cards.length;
          setLaneAnnouncement(
            added === 0
              ? `No more deals in ${STAGE_LABEL[lane]}.`
              : `Loaded ${added} more in ${STAGE_LABEL[lane]}. Showing ${onScreen}.`,
          );
        })
        .catch((error: unknown) => {
          // A failure from a query nobody is looking at any more is not an
          // error worth showing: the control it belonged to is already gone.
          if (queryGeneration.current !== generation) return;
          // The lane keeps every card it already had — the control says the
          // page did not load and stays pressable. Vanishing would read as
          // "there is no more", which is the one thing it must never imply.
          setLaneStatus((prev) => ({ ...prev, [lane]: 'error' }));
          // ⚠️ A KEYSET PAGE REFUSED OUTRIGHT MAY MEAN clinic-service WAS ROLLED
          // BACK BELOW S4.1 UNDER THIS OPEN BOARD (2026-09-26, S4.5 review): its
          // `.strict()` schema 400s `activityBefore`/`idBefore`, and "Try again"
          // would re-send the same refused read for as long as the board stayed
          // open. So page one is re-read, which decides the order again: an old
          // server flips the board to the created order and drops these kept
          // pages (the lane's error goes with them); a server that still serves
          // the activity order leaves the board, and this lane's retry, as they
          // were. The keyset read is built here and carries nothing the server
          // refuses beside a cursor (no `offset`, no `order`), so a 400 on it
          // has no other cause worth guarding against.
          if (keyset && error instanceof ApiError && error.status === 400) load();
        });
    },
    // `debouncedSearch` is not READ above (a searched board is never paged),
    // but it stays a dependency: it decides which page one these offsets are
    // counted from, so a stale closure would page past the wrong board.
    //
    // ⚠ UNREVIEWED SUPPRESSION (2026-09-05, first time this file was ever linted).
    // exhaustive-deps calls this dependency "unnecessary" and its autofix would
    // DELETE it — which is precisely the paging bug the comment above describes.
    // Kept as-is; flagged for CTO review. (`load` joined the list 2026-09-26 —
    // the keyset refusal above re-reads page one.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearch, filters, items, lanePages, laneStatus, load],
  );

  // Create new lead → the reloaded board carries the new card — open its drawer.
  useEffect(() => {
    if (!openOnCreate || !items) return;
    const card = items.find((i) => i.dealId === openOnCreate);
    if (card) setSelected(card);
  }, [openOnCreate, items]);

  const requestMove = useCallback((card: DealCard, target: ManualTarget) => {
    setNotice(null);
    setPending({ card, target });
  }, []);

  /**
   * Close as lost — its own runner, because its failure behaves differently.
   *
   * `runMove` closes the dialog and reports upward; that is right for the moves
   * whose only refusal is a surprise. This one has an EXPECTED refusal (the
   * call-round rule), so the panel stays open holding the server's sentence and
   * the reason picker the staff member needs in order to act on it.
   */
  /**
   * The unreachable move's own runner (2026-09-23). It is `runCloseAsLost`'s
   * shape rather than `runMove`'s for one reason: its dialog must stay OPEN and
   * render the refusal itself. `runMove` closes the dialog and calls
   * `setNotice`, which draws an Alert on the BOARD — behind the drawer's fixed
   * scrim and outside its `aria-modal` subtree, i.e. invisible to exactly the
   * person who pressed the button. That is the close-as-lost defect, and it
   * would land here the same way: this move has TWO server refusals (the 409
   * attempts gate and the 400 for a reason needing a note).
   *
   * It shares `closeBusy`/`closeError` with the close dialog deliberately —
   * only one manual-move dialog is ever open (`pending` is a single value), so
   * a second pair would be two names for one state and a chance for a stale
   * error to appear under the wrong panel.
   */
  async function runMarkUnreachable(pendingMove: PendingMove, reasonCode: string, detail: string) {
    setCloseBusy(true);
    setCloseError(null);
    try {
      await patchDealStage(pendingMove.card.dealId, 'unreachable', detail || undefined, reasonCode);
      setPending(null);
      setSelected(null);
      setLanePages((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([lane, held]) => [
            lane,
            held ? { ...held, cards: held.cards.filter((c) => c.dealId !== pendingMove.card.dealId) } : held,
          ]),
        ),
      );
      load();
    } catch (err) {
      setCloseError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — that did not go through. Please try again.',
      );
    } finally {
      setCloseBusy(false);
    }
  }

  async function runCloseAsLost(pendingMove: PendingMove, reasonCode: string, detail: string) {
    setCloseBusy(true);
    setCloseError(null);
    try {
      await patchDealStage(pendingMove.card.dealId, pendingMove.target.to, detail || undefined, reasonCode);
      setPending(null);
      setSelected(null);
      setLanePages((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([lane, held]) => [
            lane,
            held ? { ...held, cards: held.cards.filter((c) => c.dealId !== pendingMove.card.dealId) } : held,
          ]),
        ),
      );
      load();
    } catch (err) {
      // The SERVER'S OWN WORDS, verbatim. Its 409 names the counts and the way
      // out ("or — if you did reach them — choose the reason they gave
      // instead"); re-deriving an apology from the status code would throw away
      // the only sentence that tells somebody what to do next.
      setCloseError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — that did not go through. Please try again.',
      );
    } finally {
      setCloseBusy(false);
    }
  }

  async function runMove(pendingMove: PendingMove, fields: { reason?: string; note?: string; scheduledCallbackAt?: string; phone?: string; preferredTime?: string }) {
    const { card, target } = pendingMove;
    try {
      if (target.via === 'stage-patch') {
        await patchDealStage(card.dealId, target.to, fields.reason);
      } else if (target.via === 'handover') {
        const to = target.to === 'awaiting_admin_followup' ? 'admin' : 'clinician';
        await dealHandover(card.dealId, to, fields.note ?? '', fields.scheduledCallbackAt);
      } else {
        await convertDealToCallback(card.dealId, { phone: fields.phone || undefined, preferredTime: fields.preferredTime || undefined });
      }
      setPending(null);
      setSelected(null); // the drawer re-opens fresh from the reloaded board
      // ⚠️ A MOVED CARD LEAVES THE HELD PAGES (2026-09-04). `load()` refreshes
      // page ONE; the extra pages are deliberately kept across a refresh (staff
      // page a lane in and then work through it). But a card that has just
      // changed stage would still be sitting in whichever lane's page it was
      // fetched into, carrying its OLD stage — the board would draw it in the
      // lane it has left. Dropping it by id makes page one the only source of
      // truth for that deal: it reappears in its new lane if it is in that
      // lane's first page, and the lane's own "View more" reaches it if not.
      setLanePages((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([lane, held]) => [
            lane,
            held ? { ...held, cards: held.cards.filter((c) => c.dealId !== card.dealId) } : held,
          ]),
        ),
      );
      load();
    } catch (err) {
      setPending(null);
      // §9 — a 409 from the three-attempt closed_lost gate carries a PHI-free
      // counts sentence in `detail` (parseError on the stage PATCH): surface
      // the SERVER's message inline — it says exactly what is still required.
      // …and a 403 carries the same kind of sentence since 2026-09-22: the
      // admin-only move to `unreachable` names the desk that owns it. The
      // portal hides that row from a clinician, so reaching this needs a stale
      // board or a race — which is exactly when "refused (403)" tells somebody
      // nothing they can act on. Both codes are PHI-free by construction (a
      // stage word, a desk, counts).
      if (err instanceof ApiError && (err.status === 409 || err.status === 403) && err.detail) {
        setNotice(err.detail);
      } else {
        setNotice(err instanceof ApiError ? `Sorry — that move was refused (${err.status}).` : 'Sorry — that move did not go through.');
      }
    }
  }

  /**
   * The NAME/EMAIL filter is gone from here — it is the server's job now (see
   * the load callback). `entryPath` stays local on purpose: it is a facet of
   * cards already on screen, it needs no round trip, and pushing it to the
   * server would make a tag-toggle cost a fetch.
   */
  const query = debouncedSearch.toLowerCase();
  const filtering = query !== '' || filters.entryPaths.length > 0 || filters.stages.length > 0;
  const appliedCount = activeFilterCount(filters);
  // A search that could not run must not leave its stale results on screen
  // looking like an answer — the board reverts to what it last loaded, and the
  // banner below says the search did not happen.
  const searchApplied = query !== '' && !searchError;
  /**
   * IS THE BOARD SHOWING EVERYTHING? (2026-08-29)
   *
   * clinic-service serves ONE capped page — `DEALS_LIST_DEFAULT_TAKE = 50` for
   * all eleven lanes — while `getDealCounts()` counts the whole tenant. The
   * search above filters only what that page holds, so on a board past 50 deals
   * a perfectly real patient can be typed in by name and produce no result.
   *
   * That is not a hypothetical: it is how a paid patient sitting in Awaiting
   * clinical review came to be reported as "not there on admin". The right fix
   * is a server-side search (it needs a contracts change and is tracked
   * separately); until then the least this screen can do is stop pretending its
   * search is complete.
   *
   * `counts` may be null when that read failed — then we genuinely do not know,
   * and claiming a shortfall would be its own invention.
   */
  /**
   * WHAT THE BOARD ACTUALLY HOLDS = page one + every paged-in lane (2026-09-04).
   *
   * Page one wins on a `dealId` collision, because it is the fresher read: a
   * refresh replaces it while the extra pages are held across refreshes on
   * purpose. Extra cards are grouped by their OWN `stage` downstream (the board
   * filters `items` by stage), so nothing here has to re-derive a lane.
   */
  const boardItems = useMemo(() => {
    const base = items ?? [];
    const held = Object.values(lanePages);
    if (held.length === 0) return base;
    const seen = new Set(base.map((c) => c.dealId));
    return [...base, ...held.flatMap((page) => (page?.cards ?? []).filter((c) => !seen.has(c.dealId)))];
  }, [items, lanePages]);

  const loadedTotal = boardItems.length;
  const pipelineTotal = counts ? Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0) : null;
  // ⚠️ ONLY WHEN THE BOARD IS UNSEARCHED. With `?q=` on the wire the server has
  // looked at every deal in the tenant, so "we only searched what is loaded" is
  // no longer true — and repeating it would send staff hunting for a patient
  // the search has already told them is not there.
  // ⚠️ NOT WHILE SCOPED TO A STAGE EITHER. With `?stage=` on the wire the read
  // covers that whole stage, so "showing the N most recent of M" would compare
  // a stage's cards against the WHOLE pipeline's total and invent a shortfall
  // that is not there.
  const partialBoard =
    pipelineTotal !== null &&
    !filters.showTest &&
    /**
     * ⚠️ NO `!filters.showDuplicates` HERE, AND THAT IS CHECKED, NOT FORGOTTEN
     * (2026-09-06). The instinct is to copy the `showTest` suppression above,
     * because that one exists precisely because the badges are hard-wired
     * `isTest: false` while the LIST honours `includeTest` — so the page can
     * hold more cards than the count and "N of M" starts lying.
     *
     * Duplicates do not have that asymmetry. clinic-service hard-wires them out
     * of the badges too, EXCEPT in their own `closed_duplicate` lane, whose
     * badge is a real number — and their own lane is the only place a merged
     * card can be (marking moves it there, and the release moves it back). So
     * turning them on adds rows exactly where the totals already count them,
     * and both sides of this comparison move together. Suppressing the cue
     * anyway would withhold a true fact for a mismatch that cannot happen.
     */
    !searchApplied &&
    filters.stages.length === 0 &&
    // Nor under a reason filter: the read is narrowed inside every lane, so
    // "N of M deals" would compare the matches against the whole pipeline.
    filters.dropOffReasons.length === 0 &&
    loadedTotal < pipelineTotal;
  const visible = boardItems.filter(
    (i) => filters.entryPaths.length === 0 || filters.entryPaths.includes(i.entryPath),
  );

  /**
   * WHICH LANES GET A "VIEW MORE", AND WHAT STATE IT IS IN (2026-09-04).
   *
   * `more` is answered by the LAST PAGE FETCHED for the lane, never by
   * "rendered < total": there is no total on this wire and no `hasMore` flag,
   * and the total the badge shows is counted with `isTest: false` over the
   * whole tenant, so comparing the two is exactly the mistake that would offer
   * a page which does not exist. A lane with no extra pages yet is judged on
   * PAGE ONE — its slice of the base read, not of `boardItems`, because a full
   * page one is what says "there may be a page two".
   *
   * The board applies the two suppressions (test records shown / filtering) —
   * see `laneViewMore` there, which sits beside the count cue that refuses
   * itself in the same two states, so the two can never drift apart.
   */
  const lanePaging: BoardPaging = useMemo(() => {
    const base = items ?? [];
    const more: Partial<Record<DealStage, boolean>> = {};
    for (const lane of DEAL_STAGES) {
      more[lane] = lanePageMore(lanePages[lane], base.filter((i) => i.stage === lane).length, activityOrder.current === true);
    }
    return { more, status: laneStatus, announcement: laneAnnouncement, onLoadMore: loadMore };
  }, [items, lanePages, laneStatus, laneAnnouncement, loadMore]);

  return (
    <Card>
      {/* ══ THE TITLE LINE, WHICH NOW CARRIES THE TOOLBAR TOO ════════════════
          (2026-09-06, CTO: the board frame — "give the lanes their height
          back".)

          Search and Filters used to occupy a full-width row of their own
          UNDERNEATH this heading, which cost the board the heading's own line
          and its trailing margin — 32px, measured — for a row that was mostly
          empty: past the search box and the Filters button, the right-hand half
          was dead space on any console wider than about 900px. Seating the
          toolbar on the title line spends that dead space instead of the fold,
          and hands the height straight to the lanes, which is what puts a
          lane's pinned money total above the fold rather than below it.

          ⚠️ THE FIRST ATTEMPT AT THIS MOVED THE BLOCK AND NOT THE CONTROLS
          (2026-09-06 → corrected 2026-09-07). The toolbar block landed here as
          asked and was still TWO ROWS DEEP inside itself, so the line read
          "Pipeline    Search" with the input and the button underneath it and
          the header stayed as tall as it had ever been, minus 14px. The whole
          fix was one `display: block` on the label; the whole reason it shipped
          is that no spec in this repo can see a wrapped row. Measured before
          and after, in a browser, at 1440x900: header block 118→218 (100px)
          before, 118→190 (72px) after, and the board region starts 24px higher.
          The reasoning is on the label itself, at the point somebody would undo
          it.

          ⚠️ BASELINE-ALIGNED, NOT flex-end, AND THE CHIPS ARE WHY. The applied
          -filter chips live inside the toolbar and are allowed to wrap to three
          or four rows, so the right-hand block is the one item on this line
          whose height is not fixed. `flex-end` would tie the heading to the
          BOTTOM of that block and drag "Pipeline" halfway down the card every
          time somebody applied four filters. Baselines do not move: the heading
          sits level with the first line of the toolbar — the `Search` label and
          the input's own text — whatever the chips do underneath. This is the
          same reasoning the toolbar itself records one level down, applied one
          level up.

          ⚠️ IT WRAPS BACK TO TWO ROWS ON A NARROW SCREEN, AND THAT IS THE
          DESIGN. Below roughly the frame's own width the toolbar drops onto its
          own line and the layout is exactly what it was before this change —
          which is the point: nothing downstream may depend on the height this
          row gives back. The lane cap (LANE_MAX_HEIGHT) is a share of the
          VIEWPORT and the money total is pinned inside the lane, so both hold
          identically in the wrapped state. See TOOLBAR_INLINE_BASIS. */}
      <div
        data-testid="pipeline-title-row"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          // Column gap between title and toolbar; row gap for when they stack.
          gap: 'var(--gps-s-2) var(--gps-s-4)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flex: '0 0 auto' }}>
          {/* No trailing margin — this heading shares a line now, and the row
              owns the spacing. See SectionTitleInline for why that is a
              separate primitive and not a negative margin here.

              ⚠️ "Create new lead" LEFT THIS GROUP ON 2026-09-13 (CTO: "push the
              filters a bit and make room for the button, it should come here",
              drawn from the button across to the right of Filters). It is the
              row's only ACTION — everything else on the line reads or narrows
              the board — and it now sits at the end of the toolbar, where a
              primary action belongs and where the eye finishes the row. The
              heading keeps this group to itself. */}
          <SectionTitleInline>Pipeline</SectionTitleInline>
        </div>

        {/* The toolbar block. ONE ROW DEEP, and that is the fix of 2026-09-07 —
            see the `Search` label below for what a second row in here cost. */}
        <div style={{ flex: `1 1 ${TOOLBAR_INLINE_BASIS}`, minWidth: 0 }}>

          {/* ── The filter toolbar (replaces the "SQL deals" toggle) ─────────── */}
          {/* ⚠️ THE HELPER TEXT IS NOT IN THIS ROW (2026-08-31, CTO: "I would move
              search in both pipeline and patients screen here so we can keep both
              filters and search in the same line").

              It used to be. The search column held label + input + caveat + error,
              so `alignItems: flex-end` aligned the buttons to the bottom of THAT
              WHOLE COLUMN — which is why Filters rendered a line below the input,
              level with the caveat sentence, instead of beside the box. No
              alignment value fixes that; the column has to stop being tall. The
              caveat and the error now sit on their own full-width row underneath,
              where they still read in order and still describe the input. */}
          {/* ⚠️ TOP-ALIGNED, NOT CENTRED (2026-09-05). Every CONTROL on this row
              is CONTROL_ROW_HEIGHT, and among items of one height top-alignment and
              centring draw the same picture — the invariant was always the shared
              height, never the property that aligns it. What changed is that the row
              now also carries the applied-filter chips, and that block is the one
              item allowed to be TALLER than a control: at "everything applied" it
              wraps to three or four chip rows. Centring against it would drag the
              search input to the middle of a four-row bar and out of line with the
              `Search` label beside it. Top-aligned, the label, the input and the
              button keep one top edge however far the chips grow.

              ⚠️ AND IT PACKS TO THE ROW'S END (2026-09-07, CTO drew a blue line at
              the right edge of the header: "move these search and filter to the top
              so you save more space, then align to end that row"). Without
              `justify-content` the controls sat at the START of a block that
              stretches to the Card's edge, so on a 1440px console the Filters button
              finished 484px short of the line he drew — measured — and the space it
              was supposed to be using was the dead space to its right. The chip
              block still takes that slack when filters ARE applied (`FilterChipRow`
              grows), which is why this only shows itself on the unfiltered board:
              one state was right and the other was 484px wrong, from one missing
              declaration. */}
          <div
            data-testid="pipeline-toolbar"
            style={{
              display: 'flex',
              gap: 'var(--gps-s-3)',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            {/* ⚠️⚠️ THE `Search` LABEL IS AN ITEM IN THIS ROW. IT WAS A
                `display: block` HEADING ABOVE IT UNTIL 2026-09-07, AND THAT ONE
                DECLARATION IS WHY THE 2026-09-06 "MOVE THE TOOLBAR ONTO THE TITLE
                LINE" CHANGE DID NOT LAND.

                What that change moved was this block — the CONTAINER — onto the
                heading's line. The container was two rows deep: a full-width label,
                then the controls. So the title line ended up carrying "Pipeline"
                and the WORD "Search", the input and the Filters button stayed on a
                second row underneath, and the board got back the heading's 14px
                margin and nothing else. Measured in a browser at 1440x900 before
                the fix: heading 118→139, label 121→140 at 995px WIDE (the full
                width of this block — a block-level label in a block-level box takes
                the whole line whatever is beside it), input and button 146→194. Two
                rows, exactly as the CTO's screenshot showed, with every assertion in
                `boardFrame.spec.tsx` green — containment, `flex-grow`, `flex-wrap`
                and `align-items` were all true of a layout that looked wrong,
                because jsdom does no layout and none of them could see a wrap.

                It is INLINE now, first in the row, still a real `<label htmlFor>`:
                the input's accessible name, the visible word and the click-to-focus
                target are all unchanged — it is the box it renders in that moved,
                for the second time and in the same direction. It carries
                CONTROL_ROW_HEIGHT and centres its own text, so it obeys the "one
                control row, one height" rule the block below states: a 19px label
                top-aligned against a 48px input would have floated at the input's
                top edge. Anything that would make this column TALLER than one
                control (a hint, a caveat, an error) still goes on its own full-width
                row underneath — see the two rows below the header. */}
            <label
              htmlFor="pipeline-search"
              style={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                minHeight: CONTROL_ROW_HEIGHT,
                fontSize: '12.5px',
                fontWeight: 600,
                color: 'var(--gps-muted)',
              }}
            >
              Search
            </label>
            {/* ⚠️ NOTHING GOES BACK IN THIS COLUMN THAT CHANGES ITS HEIGHT. The
                block above records the label's two moves; the block above THAT
                records the helper text and the search error leaving it in 2026-08-31
                for the same reason. It holds exactly one 48px input, and the row's
                alignment depends on it staying that way — a hint, a caveat, an
                inline error or a second `<Field>` here re-breaks the row and no
                alignment property will fix it. Anything that describes the input
                goes on its own full-width row underneath. */}
            {/* ⚠️ `maxWidth` TRIMMED 420 → 360 with the button's arrival. The
                input is the row's only grower, so on a wide console it took all
                the slack and pushed Filters and the new CTA against the right
                edge with no breathing room between them. 360px still holds a
                full email address. */}
            <div style={{ flex: '1 1 240px', maxWidth: '360px' }}>
              <TextInput
                id="pipeline-search"
                type="search"
                placeholder="Name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-describedby={partialBoard ? 'pipeline-search-scope' : undefined}
                style={{ height: CONTROL_ROW_HEIGHT }}
              />
            </div>
            {/* ⚠️ ONE CONTROL ROW, ONE HEIGHT (2026-08-30 → 2026-09-04, CTO twice:
                "the top bar input fields and filter and button is not nicely
                aligned", then "Search, Filters and the red X do not line up").

                The bar used to hold two LABELLED fields (label above input) beside
                two bare buttons. A labelled field is taller than a button by the
                height of its label, so nothing on the row could share a baseline
                whatever alignment rule was applied — `flex-end` lined up the
                bottoms and left the tops ragged, `baseline` did the reverse. The
                2026-08-30 pass moved everything but Search into the panel, which
                got the row down to one labelled field and two buttons; the
                2026-08-31 pass moved the caveat and the error out of the search
                column.

                What was left was three controls of three heights (48 / 44 / 44)
                still leaning on an alignment property, which is why they still read
                as three unrelated things. `alignSelf: flex-end` is GONE with them:
                every control on the row now carries CONTROL_ROW_HEIGHT and the row
                centres them, so there is no last-2px correction left to make. */}
            {/* ⚠️ THE BUTTON MUST LOOK SELECTED, NOT JUST COUNT (2026-08-31, CTO:
                "if filters applied the filters button should be selected to
                indicate there are filters applied. and red X indicating you can
                click on that to remove"), and THE CLEAR IS A SEPARATE CONTROL —
                one target doing two opposite things is worse than two targets.

                Both rules, and the tinted-applied palette that carries the first
                one, now live inside `FiltersTrigger` in `ui/filters.tsx` rather
                than being re-stated here. That lift is the point: the Patients
                directory draws the same control from the same module, so the
                grammar cannot fork the next time a staff screen grows a filter.
                The button's own WHY travels with it — read it there. */}
            <FiltersTrigger
              appliedCount={appliedCount}
              onOpen={() => setFiltersOpen(true)}
              onClearAll={() => setFilters(EMPTY_FILTERS)}
            />

            {/* THE ROW'S ONE ACTION, LAST (CTO, 2026-09-13). Search and Filters
                narrow what is already on the board; this is the only control
                that makes something. It carries CONTROL_ROW_HEIGHT like every
                other control on the line — see the "one control row, one height"
                block above — so it shares their top edge rather than needing an
                alignment correction of its own.

                ⚠️ `flex: '0 0 auto'` AND IT MUST STAY THAT WAY. The search input
                beside it is the row's only grower (`1 1 260px`); a button that
                could also grow would take the slack the input needs and leave a
                stretched CTA beside a shrunken search box. */}
            {role === 'CLINICIAN' ? (
              <Button
                type="button"
                variant="cta"
                style={{ ...compactButton, flex: '0 0 auto', minHeight: CONTROL_ROW_HEIGHT, whiteSpace: 'nowrap' }}
                onClick={() => setCreating(true)}
              >
                Create new lead
              </Button>
            ) : null}

            {/* ⚠️ APPLIED FILTERS ARE CHIPS, NOT BUTTONS (2026-08-30, CTO: "there
                is no difference between the Clear button and how it shows the
                applied filter").

                Before this, an applied stage and the Clear control were two
                same-weight ghost buttons side by side — one was STATE and the other
                was an ACTION, and nothing said which. Chips are pill-shaped, tinted,
                and each carries its own remove affordance, so they read as "here is
                what is on" rather than "here are two things to press". Nothing else
                on this bar is chip-shaped, which is what makes the distinction carry
                without a legend.

                ⚠️ AND THEY BELONG IN THIS ROW, NOT ON ONE OF THEIR OWN (2026-09-05,
                CTO: the chips "crowd" the lane headers, and there is "a large empty
                area to the right of the Filters button and the clear ×").

                Both complaints were the same block. The chip row was a plain
                in-flow sibling between the bar and the board carrying 12px above
                itself and NOTHING below, so it sat clear of the toolbar and flush
                against "NEW LEAD 4" — it read as part of the board it was filtering
                rather than as part of the bar that set it. Meanwhile everything
                right of the × was dead space, on the order of 700px of it on a
                1600px console. Seating the chips there answers both at once, and it
                DELETES the offending block rather than tuning its margin.

                ⚠️ THE HEADERS' BREATHING ROOM IS A SEPARATE FIX, and it had to be:
                the 0px above them was never the chips' doing. Nothing from this row
                down to the lane `<h3>` set any bottom spacing, so that gap was 0
                with the chips, without them, and with the search caveat in their
                place — applying a filter only made an already-flush junction
                obvious. See the board region further down.

                ⚠️ THE SIZING IS `FilterChipRow`'S, NOT THIS FILE'S (2026-09-05).
                The `flex: '1 1 340px'` + `minWidth: 0` + own `flexWrap` that makes
                the block take the row's slack, wrap downward and still let the
                search box keep its line is one tuned declaration, and the Patients
                bar needs exactly the same one. It lives in `ui/filters.tsx` with
                the browser measurements that produced the 340px threshold written
                beside it, so a screen cannot half-copy it. Change it there and both
                bars move together; change it here and you have forked the grammar
                this module was lifted to stop forking. */}
            {appliedCount > 0 ? (
              <FilterChipRow>
                {filters.stages.map((stage) => (
                  <FilterChip
                    key={stage}
                    label={STAGE_LABEL[stage]}
                    onRemove={() => setFilters((f) => ({ ...f, stages: f.stages.filter((s) => s !== stage) }))}
                  />
                ))}
                {filters.entryPaths.map((path) => (
                  <FilterChip
                    key={path}
                    label={ENTRY_LABEL[path]}
                    onRemove={() => setFilters((f) => ({ ...f, entryPaths: f.entryPaths.filter((p) => p !== path) }))}
                  />
                ))}
                {filters.showTest ? (
                  <FilterChip
                    label="Test records shown"
                    onRemove={() => setFilters((f) => ({ ...f, showTest: false }))}
                  />
                ) : null}
                {filters.showDuplicates ? (
                  <FilterChip
                    label="Merged duplicates shown"
                    onRemove={() => setFilters((f) => ({ ...f, showDuplicates: false }))}
                  />
                ) : null}
                {filters.dropOffReasons.map((reason) => (
                  <FilterChip
                    key={reason}
                    label={dropOffFilterChipLabel(reason)}
                    onRemove={() =>
                      setFilters((f) => ({ ...f, dropOffReasons: f.dropOffReasons.filter((r) => r !== reason) }))
                    }
                  />
                ))}
                {/* NO "Clear all" HERE ANY MORE (2026-08-31). It lived in this row
                    and only appeared past TWO filters, so with exactly one applied
                    the only reset was that chip's own × — which is what "filter reset
                    option should be outside as well" was describing. The × beside the
                    Filters button now covers every count, and a second clear-all in
                    this row would just be a rival for it. */}
              </FilterChipRow>
            ) : null}
          </div>
        </div>
      </div>

      {/* The search caveat and the search error, on their own row so the row
          above can hold one baseline. Order matters: the error is the more
          urgent of the two, so it comes first.

          ⚠️ STILL FULL-WIDTH ROWS OF THEIR OWN, and they must stay that way now
          that the toolbar has moved up onto the title line. Folding either of
          them into the toolbar block would put a growing paragraph inside a
          flex item that the heading is baseline-aligned against — the exact
          "the search column has to stop being tall" failure recorded above,
          one level up and harder to see.

          ⚠️ AND THE "Showing up to 25 per stage" LINE STAYS DOWN HERE TOO — a
          considered decision, 2026-09-07, when the toolbar went up. Three
          reasons, in order of how much they cost to get wrong:
            · it is the search input's `aria-describedby`, so it has to FOLLOW
              the input in reading order for a screen reader to hear it as a
              description of the box rather than as a stray sentence;
            · it is a SENTENCE that grows — two clauses and five digits — and
              the header line is the one place in this Card where a growing
              text node re-breaks the layout (see above, twice);
            · it costs one 18px line, measured, and it is the only thing on the
              screen that admits the board is truncated. The 28px the toolbar
              gave back is not worth buying by making the truncation quieter.
          Measured: ONE 18px line on a working console (1082px of content at a
          1440px viewport, 729px at 1071px), and two lines — 36px — only down at
          the 768px `DesktopOnly` floor, where the header itself has already
          gone two-row. That is the one place a compact line is worth having and
          it is already compact there. */}
      {searchError ? (
        <p
          role="status"
          style={{ margin: '6px 0 0', fontSize: '12.5px', lineHeight: 1.45, color: 'var(--gps-danger-text)' }}
        >
          That search could not run, so the board below is unchanged. Try a shorter term, or reload
          the page.
        </p>
      ) : null}
      {partialBoard ? (
        <p
          id="pipeline-search-scope"
          style={{ margin: '6px 0 0', fontSize: '12.5px', lineHeight: 1.45, color: 'var(--gps-muted)' }}
        >
          Showing up to {BOARD_PER_LANE} per stage, {loadedTotal} of {pipelineTotal} deals in total.
          Search by name, email or reference to look through all of them.
        </p>
      ) : null}

      {filtersOpen ? (
        <PipelineFilters
          counts={counts}
          applied={filters}
          onApply={(next) => {
            setFilters(next);
            setFiltersOpen(false);
          }}
          onClose={() => setFiltersOpen(false)}
        />
      ) : null}

      {notice ? (
        <Alert tone="warning" role="status" style={{ margin: 'var(--gps-s-2) 0' }}>
          {notice}
        </Alert>
      ) : null}

      {/* ⚠️ THE BOARD OWNS THE GAP ABOVE ITSELF (2026-09-05, CTO: the applied
          filters "crowd" the lane headers, "NEW LEAD 4" and "CALLBACK 9" sitting
          tight against them).

          The obvious reading is that the chip row was too close and wants a
          `marginBottom`. It is the wrong diagnosis, and a margin there would
          have fixed the screenshot without fixing the bug: `PipelineBoard`'s
          root div, its grid scroller and every lane `<h3>` all carry zero top
          spacing, and nothing in this Card set any bottom spacing either — so
          the headers sat flush against whatever last rendered above them. With
          no filters applied that is the toolbar; with a truncated board it is
          the search caveat; the gap was 0px in every one of those states, and
          applying a filter only put something conspicuous in the 0px.

          One wrapper, one margin, and all three states below (skeleton, error
          and board) clear the bar by the same amount at every chip count —
          including a chip block that has wrapped to four rows, since the margin
          is measured from whatever the toolbar's own height turned out to be. */}
      <div data-testid="pipeline-board-region" style={{ marginTop: 'var(--gps-s-5)' }}>
        {status === 'loading' ? (
          // Wave 6 §1: the loading state is board-shaped — one skeleton lane per
          // real stage, so the swap to the loaded board is a fill-in, not a jump.
          <BoardSkeleton lanes={DEAL_STAGES.length} label="Loading the pipeline…" />
        ) : status === 'error' || !items ? (
          <ErrorState message="Sorry — we could not load the pipeline." onRetry={load} />
        ) : (
          <PipelineBoard
            items={visible}
            counts={counts}
            laneMoney={laneMoney}
            role={role}
            showTest={filters.showTest}
            filtered={filtering && (searchApplied || filters.entryPaths.length > 0)}
            reasonFilter={filters.dropOffReasons.length > 0}
            onOpen={setSelected}
            onRequestMove={requestMove}
            paging={lanePaging}
          />
        )}
      </div>

      {selected ? (
        <DealDrawer
          key={selected.dealId}
          card={selected}
          initialTab={selected.dealId === openOnCreate ? 'questionnaire' : 'overview'}
          onClose={() => {
            setSelected(null);
            setOpenOnCreate(null);
          }}
          onChanged={load}
          onRequestMove={requestMove}
          onOpenDeal={openDealById}
        />
      ) : null}

      {/* ── The one home of every manual-move dialog ─────────────────────── */}

      {/* CLOSE AS LOST has its own panel: a reason PICKER rather than a prose
          box (free text cannot be reported on), and a refusal rendered where the
          person clicking is looking. Every other stage-patch keeps the shared
          confirm. */}
      {pending?.target.via === 'stage-patch' && pending.target.to === 'closed_lost' ? (
        <CloseAsLostDialog
          dealName={dealName(pending.card)}
          attemptsThisRound={pending.card.attemptsThisRound ?? null}
          busy={closeBusy}
          serverError={closeError}
          onConfirm={(reasonCode, detail) => void runCloseAsLost(pending, reasonCode, detail)}
          onCancel={() => {
            setPending(null);
            setCloseError(null);
          }}
        />
      ) : null}

      {/* MARK UNREACHABLE has its own panel for the same reasons as the close
          above: a reason PICKER rather than a prose box, and a refusal rendered
          where the person clicking is looking. */}
      {pending?.target.via === 'stage-patch' && pending.target.to === 'unreachable' ? (
        <MarkUnreachableDialog
          dealName={dealName(pending.card)}
          stage={pending.card.stage}
          attemptsThisRound={pending.card.attemptsThisRound ?? null}
          busy={closeBusy}
          serverError={closeError}
          onConfirm={(reasonCode, detail) => void runMarkUnreachable(pending, reasonCode, detail)}
          onCancel={() => {
            setPending(null);
            setCloseError(null);
          }}
        />
      ) : null}

      {pending?.target.via === 'stage-patch' &&
      pending.target.to !== 'closed_lost' &&
      pending.target.to !== 'unreachable' ? (
        <ConfirmDialog
          open
          title={`Move ${dealName(pending.card)} to ${STAGE_LABEL[pending.target.to]}`}
          confirmLabel="Move"
          requireReason={pending.target.requiresReason}
          reasonLabel="Reason"
          onConfirm={(reason) => runMove(pending, { reason: reason ?? undefined })}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {pending?.target.via === 'handover' ? (
        <HandoverDialog
          card={pending.card}
          toClinician={pending.target.to === 'awaiting_clinician_followup'}
          onConfirm={(note, scheduledCallbackAt) => runMove(pending, { note, scheduledCallbackAt })}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {pending?.target.via === 'convert' ? (
        <ConvertDialog
          card={pending.card}
          onConfirm={(phone, preferredTime) => runMove(pending, { phone, preferredTime })}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {creating ? (
        <CreateDealDialog
          onCreated={(created) => {
            setCreating(false);
            setOpenOnCreate(created.dealId);
            load();
          }}
          onCancel={() => setCreating(false)}
        />
      ) : null}
    </Card>
  );
}

/**
 * The bidirectional handover gate: a REQUIRED note (it lands as a real
 * callback activity, visible to both sides) and — hand-BACK only — the
 * optional "patient asked to be called in X" TIME BLOCK the clinician then
 * sees (UX Wave 9 §5: the five-block Select replaced the datetime picker;
 * the wire value is the block string, which the server validates).
 */
function HandoverDialog({
  card,
  toClinician,
  onConfirm,
  onCancel,
}: {
  card: DealCard;
  toClinician: boolean;
  onConfirm: (note: string, scheduledCallbackAt?: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [note, setNote] = useState('');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || note.trim().length < 3) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(note.trim(), when || undefined);
    } catch {
      setError('Sorry — the handover did not go through.');
      setBusy(false);
    }
  }

  return (
    <DialogShell title={toClinician ? `Hand ${dealName(card)} back to a clinician` : `Hand ${dealName(card)} over to admin`} onCancel={onCancel}>
      <form onSubmit={submit} aria-label="Handover">
        <Field label="Handover note (required — it joins the activity trail)" htmlFor="deal-handover-note">
          <TextInput id="deal-handover-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {toClinician ? (
          <Field label="Patient asked to be called (optional)" htmlFor="deal-handover-when">
            <Select id="deal-handover-when" value={when} onChange={(e) => setWhen(e.target.value)}>
              <option value="">No preferred window</option>
              {TIME_BLOCKS.map((block) => (
                <option key={block} value={block}>
                  {TIME_BLOCK_LABELS[block]}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {error ? (
          <p role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
            {error}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="submit" variant="primary" disabled={busy || note.trim().length < 3} style={compactButton}>
            {busy ? 'Handing over…' : 'Hand over'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} style={compactButton}>
            Cancel
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

/** The "want our clinician to call you?" conversion — mints a REAL ticket. */
function ConvertDialog({
  card,
  onConfirm,
  onCancel,
}: {
  card: DealCard;
  onConfirm: (phone: string, preferredTime: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [phone, setPhone] = useState(card.contact.phone ?? '');
  const [preferredTime, setPreferredTime] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(phone.trim(), preferredTime.trim());
    } catch {
      setError('Sorry — the conversion did not go through.');
      setBusy(false);
    }
  }

  return (
    <DialogShell title={`Convert ${dealName(card)} to a callback`} onCancel={onCancel}>
      <form onSubmit={submit} aria-label="Convert to callback">
        <Field label="Phone number" htmlFor="deal-convert-phone">
          <TextInput id="deal-convert-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        {/* Wave 9 §5 — block Select, no free text (the server refuses it). */}
        <Field label="Preferred time (optional)" htmlFor="deal-convert-time">
          <Select id="deal-convert-time" value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)}>
            <option value="">No preference</option>
            {TIME_BLOCKS.map((block) => (
              <option key={block} value={block}>
                {TIME_BLOCK_LABELS[block]}
              </option>
            ))}
          </Select>
        </Field>
        {error ? (
          <p role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
            {error}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="submit" variant="primary" disabled={busy} style={compactButton}>
            {busy ? 'Converting…' : 'Create the callback'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} style={compactButton}>
            Cancel
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

/** Clinician "Create new lead" — the SQL sales lane (contact + deal; the drawer opens onto the questionnaire). */
function CreateDealDialog({ onCreated, onCancel }: { onCreated: (created: DealCreated) => void; onCancel: () => void }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createDeal({ clinicSlug: 'weightloss', email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() });
      onCreated(created);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? 'This contact already has an open deal — find them on the board instead.'
          : 'Sorry — we could not create the deal. Check the details and try again.',
      );
      setBusy(false);
    }
  }

  return (
    <DialogShell title="Create new lead" onCancel={onCancel}>
      <form onSubmit={submit} aria-label="Create new lead">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)' }}>
          <Field label="First name" htmlFor="deal-new-first">
            <TextInput id="deal-new-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </Field>
          <Field label="Last name" htmlFor="deal-new-last">
            <TextInput id="deal-new-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Field>
        </div>
        <Field label="Email" htmlFor="deal-new-email">
          <TextInput id="deal-new-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Phone" htmlFor="deal-new-phone">
          <TextInput id="deal-new-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        {error ? (
          <p role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
            {error}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="submit" variant="cta" disabled={busy} style={compactButton}>
            {busy ? 'Creating…' : 'Create lead'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} style={compactButton}>
            Cancel
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
